import type { Event, RSVP, TicketType, Course } from '@/types';
import {
  normalizeTicketDataToFormatA,
  DEFAULT_INSTANCE_INDEX,
} from '@/lib/ticket-courses';

type EventLike = Pick<Event, 'tickets' | 'courses'> | null | undefined;

function buildLegacyMealFallback(rsvp: RSVP | null | undefined): string {
  if (!rsvp?.responses) {
    return '';
  }

  return (
    rsvp.responses.Meal ??
    rsvp.responses.meal ??
    rsvp.responses.Dinner ??
    rsvp.responses.Main ??
    rsvp.responses.main ??
    ''
  );
}

function findSelectedTicketId(rsvp: RSVP | null | undefined, tickets: TicketType[] | undefined): string | null {
  if (!rsvp?.responses || !Array.isArray(rsvp.responses.Tickets) || !tickets || tickets.length === 0) {
    return null;
  }

  const ticketIds = tickets.map((ticket) => ticket.id);

  for (const entry of rsvp.responses.Tickets as Array<Record<string, unknown>>) {
    const rawId = entry?.id;
    if (typeof rawId === 'string' && ticketIds.includes(rawId)) {
      return rawId;
    }
  }

  return null;
}

function collectCourseSelections(
  ticket: TicketType | undefined,
  courseSelections: Record<string, string> | null
): string[] {
  if (!ticket || !ticket.courses || ticket.courses.length === 0 || !courseSelections) {
    return [];
  }

  const selections: string[] = [];
  ticket.courses.forEach((course: Course) => {
    const choice = courseSelections[course.id];
    if (typeof choice === 'string' && choice.trim().length > 0) {
      selections.push(choice.trim());
    }
  });

  return selections;
}

/** "Standard x2, VIP x1" summary for multi-tenant guests, who have no meal choices. */
export function resolveGuestTicketSummary(
  rsvp: RSVP | null,
  event: EventLike,
  selectionJoiner = ', '
): string {
  const lines = Array.isArray(rsvp?.responses?.Tickets) ? (rsvp!.responses!.Tickets as Array<Record<string, unknown>>) : [];
  const parts = lines
    .filter((line) => typeof line?.qty === 'number' && (line.qty as number) > 0)
    .map((line) => `${line.name as string} x${line.qty as number}`);
  return parts.join(selectionJoiner);
}

export function resolveGuestMealChoice(
  rsvp: RSVP | null,
  event: EventLike,
  /** Join string when multiple per-ticket course selections exist (default: comma space). */
  selectionJoiner = ', '
): string {
  if (!rsvp || !event) {
    return buildLegacyMealFallback(rsvp);
  }

  const legacyFallback = buildLegacyMealFallback(rsvp);
  const tickets = event.tickets;

  if (!tickets || tickets.length === 0) {
    if (event.courses && event.courses.length > 0) {
      const mainCourse =
        event.courses.find((course) => course.name.toLowerCase().includes('main')) ?? event.courses[0];
      if (mainCourse?.name) {
        return (rsvp.responses?.[mainCourse.name] as string) ?? legacyFallback;
      }
    }
    return legacyFallback;
  }

  const selectedTicketId = findSelectedTicketId(rsvp, tickets);
  if (!selectedTicketId) {
    return legacyFallback;
  }

  const ticketDefinition = tickets.find((ticket) => ticket.id === selectedTicketId);
  if (!ticketDefinition) {
    return legacyFallback;
  }

  const ticketCourses = rsvp.responses?.TicketCourses;
  const rawSelections =
    ticketCourses && typeof ticketCourses === 'object'
      ? ticketCourses[selectedTicketId]
      : null;
  const normalizedTicketData = normalizeTicketDataToFormatA(rawSelections);
  const instanceSelections =
    normalizedTicketData?.[DEFAULT_INSTANCE_INDEX] ?? {};
  const selections = collectCourseSelections(ticketDefinition, instanceSelections);

  if (selections.length > 0) {
    return selections.join(selectionJoiner);
  }

  return legacyFallback;
}

